<template>
  <b-modal
    :id="modalId"
    :visible="visible"
    size="xl"
    centered
    no-close-on-backdrop
    hide-footer
    modal-class="media-picker-modal"
    content-class="media-picker-modal__content"
    body-class="media-picker-modal__body"
    @hide="$emit('close')"
  >
    <template #modal-title>
      <div class="d-flex align-items-center justify-content-between w-100 pr-1">
        <div>
          <h4 class="mb-0">{{ title }}</h4>
          <small class="text-muted">
            {{ multiple ? 'Select one or more media items' : 'Select a single media item' }}
          </small>
        </div>
      </div>
    </template>

    <div class="media-picker">
      <div class="media-picker__main">
        <div class="media-toolbar">
          <div class="media-toolbar__tabs">
            <b-button
              size="sm"
              :variant="tab === 'library' ? 'primary' : 'outline-primary'"
              class="media-btn media-btn--tab"
              @click="tab = 'library'"
            >
              Library
            </b-button>

            <b-button
              size="sm"
              :variant="tab === 'upload' ? 'primary' : 'outline-primary'"
              class="media-btn media-btn--tab"
              @click="tab = 'upload'"
            >
              Upload
            </b-button>
          </div>

          <div v-if="tab === 'library'" class="media-toolbar__filters">
            <b-input-group size="sm" class="mr-1 media-search">
              <b-form-input
                v-model.trim="search"
                placeholder="Search media..."
                @input="onSearchInput"
              />
            </b-input-group>

            <b-form-select
              v-model="source"
              :options="sourceOptions"
              size="sm"
              class="mr-1 media-type-filter"
              @change="onFilterChange"
            />

            <b-form-select
              v-model="type"
              :options="typeOptions"
              size="sm"
              class="mr-1 media-type-filter"
              @change="onFilterChange"
            />

            <b-form-select
              v-model="collection"
              :options="collectionOptions"
              size="sm"
              class="mr-1 media-type-filter"
              @change="onFilterChange"
            />

            <b-button
              size="sm"
              variant="outline-secondary"
              class="media-btn media-btn--ghost"
              @click="fetchLibrary"
            >
              Refresh
            </b-button>
          </div>
        </div>

        <div v-if="tab === 'upload'" class="media-upload-tab">
          <div
            class="media-dropzone"
            :class="{ 'is-dragging': isDragging }"
            @dragenter.prevent="isDragging = true"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
          >
            <div class="media-dropzone__inner">
              <feather-icon icon="UploadCloudIcon" size="34" class="mb-1 text-primary" />
              <h5 class="mb-50">Drop files here or browse</h5>
              <p class="text-muted mb-1">
                Supports multiple files. Best for images.
              </p>
              <b-button variant="primary" size="sm" class="media-btn media-btn--primary" @click="$refs.fileInput.click()">
                Choose Files
              </b-button>

              <input
                ref="fileInput"
                type="file"
                class="d-none"
                :multiple="multipleUpload"
                @change="handleFileInput"
              />
            </div>
          </div>

          <div v-if="uploadQueue.length" class="mt-2">
            <h6 class="mb-1">Selected for upload</h6>

            <div class="upload-queue">
              <div
                v-for="(file, index) in uploadQueue"
                :key="`${file.name}-${index}`"
                class="upload-queue__item"
              >
                <div class="upload-queue__item-left">
                  <feather-icon icon="FileIcon" size="16" class="mr-50" />
                  <span class="text-truncate">{{ file.name }}</span>
                </div>
                <small class="text-muted">{{ formatFileSize(file.size) }}</small>
              </div>
            </div>

            <div class="mt-2 d-flex align-items-center">
              <b-button
                variant="primary"
                size="sm"
                class="media-btn media-btn--primary"
                :disabled="isUploading"
                @click="uploadSelectedFiles"
              >
                <b-spinner v-if="isUploading" small class="mr-50" />
                {{ isUploading ? 'Uploading...' : 'Upload Now' }}
              </b-button>

              <b-button
                variant="outline-secondary"
                size="sm"
                class="media-btn media-btn--ghost ml-1"
                :disabled="isUploading"
                @click="clearUploadQueue"
              >
                Clear
              </b-button>
            </div>
          </div>
        </div>

        <div v-else class="media-library-tab">
          <div class="media-library-meta mb-1">
            <div class="text-muted small">
              <span v-if="pagination.total">
                {{ pagination.from || 0 }}-{{ pagination.to || 0 }} of {{ pagination.total }}
              </span>
              <span v-else>No media found</span>
            </div>

            <div class="text-muted small" v-if="multiple">
              Selected: {{ selectedItems.length }}
            </div>
          </div>

          <div v-if="isLoading" class="media-grid-loading">
            <b-spinner variant="primary" />
          </div>

          <div v-else-if="mediaItems.length" class="media-grid">
            <div
              v-for="item in mediaItems"
              :key="item.id"
              class="media-grid__item"
              :class="{
                'is-selected': isSelected(item),
                'is-active-preview': activePreview && activePreview.id === item.id,
              }"
              @click="handleMediaClick(item)"
              @dblclick="quickSelect(item)"
              @contextmenu.prevent="activePreview = item"
            >
              <div class="media-grid__thumb">
                <img
                  v-if="isImage(item)"
                  :src="item.preview_url || item.original_url"
                  :alt="item.file_name || item.name"
                  loading="lazy"
                  @error="handleImageError"
                />
                <div v-else class="media-grid__file">
                  <feather-icon icon="FileTextIcon" size="26" />
                </div>

                <div v-if="isSelected(item)" class="media-grid__check">
                  <feather-icon icon="CheckIcon" size="16" />
                </div>
              </div>

              <div class="media-grid__meta">
                <div class="media-grid__name">
                  {{ item.file_name || item.name }}
                </div>
                <div class="media-grid__sub">
                  {{ humanMime(item.mime_type) }}
                </div>
              </div>
            </div>
          </div>

          <div v-else class="media-empty-state">
            <feather-icon icon="ImageIcon" size="34" class="mb-1 text-muted" />
            <h5 class="mb-50">No media found</h5>
            <p class="text-muted mb-0">Try uploading files or changing the search.</p>
          </div>

          <div
            v-if="pagination.total > pagination.per_page"
            class="media-pagination mt-2"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="pagination.total"
              :per-page="pagination.per_page"
              first-number
              last-number
              size="sm"
              align="center"
              @input="onPageChange"
            />
          </div>
        </div>
      </div>

      <div class="media-picker__sidebar">
        <div class="media-sidebar-card">
          <h5 class="mb-1">Details</h5>

          <div v-if="activePreview">
            <div class="media-sidebar-preview mb-1">
              <img
                v-if="isImage(activePreview)"
                :src="activePreview.original_url || activePreview.preview_url"
                :alt="activePreview.file_name || activePreview.name"
                @error="handleImageError"
              />
              <div v-else class="media-sidebar-file-preview">
                <feather-icon icon="FileTextIcon" size="30" />
              </div>
            </div>

            <ul class="media-sidebar-info list-unstyled mb-0">
              <li><strong>Name:</strong><span>{{ activePreview.file_name || activePreview.name || '--' }}</span></li>
              <li><strong>Type:</strong><span>{{ activePreview.mime_type || '--' }}</span></li>
              <li><strong>Collection:</strong><span>{{ activePreview.collection_name || '--' }}</span></li>
              <li><strong>Size:</strong><span>{{ formatFileSize(activePreview.size || 0) }}</span></li>
              <li><strong>ID:</strong><span>{{ activePreview.id }}</span></li>
            </ul>
          </div>

          <div v-else class="text-muted small">
            Select a media item to preview its details.
          </div>
        </div>

        <div class="media-sidebar-card mt-1">
          <h5 class="mb-1">Selection</h5>

          <div v-if="selectedItems.length">
            <div
              v-for="item in selectedItems"
              :key="`selected-${item.id}`"
              class="media-selected-item"
            >
              <div class="media-selected-item__left">
                <div class="media-selected-item__thumb">
                  <img
                    v-if="isImage(item)"
                    :src="item.preview_url || item.original_url"
                    :alt="item.file_name || item.name"
                    @error="handleImageError"
                  />
                  <div v-else class="media-selected-item__thumb-fallback">
                    <feather-icon icon="FileTextIcon" size="14" />
                  </div>
                </div>

                <div class="media-selected-item__meta">
                  <div class="media-selected-item__name">
                    {{ item.file_name || item.name }}
                  </div>
                  <small class="text-muted">{{ humanMime(item.mime_type) }}</small>
                </div>
              </div>

              <b-button
                size="sm"
                variant="flat-danger"
                class="btn-icon"
                @click="removeSelected(item)"
              >
                <feather-icon icon="XIcon" size="14" />
              </b-button>
            </div>
          </div>

          <div v-else class="text-muted small">
            No media selected yet.
          </div>
        </div>
      </div>
    </div>

    <div class="media-picker-footer">
      <div class="text-muted small">
        <span v-if="multiple">{{ selectedItems.length }} item(s) selected</span>
        <span v-else>{{ selectedItems.length ? '1 item selected' : 'No item selected' }}</span>
      </div>

      <div class="d-flex align-items-center">
        <b-button
          variant="outline-secondary"
          class="media-btn media-btn--ghost mr-1"
          @click="$emit('close')"
        >
          Cancel
        </b-button>

        <b-button
          variant="primary"
          class="media-btn media-btn--primary"
          :disabled="!selectedItems.length"
          @click="confirmSelection"
        >
          {{ confirmButtonText }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { fetchMedia, uploadMedia } from '@/api/media'

let searchDebounce = null

export default {
  name: 'MediaPickerModal',
  props: {
    visible: { type: Boolean, default: false },
    modalId: { type: String, default: 'media-picker-modal' },
    title: { type: String, default: 'Media Library' },
    multiple: { type: Boolean, default: false },
    multipleUpload: { type: Boolean, default: true },
    preselected: { type: Array, default: () => [] },
    confirmButtonText: { type: String, default: 'Use Selected Media' },
    perPage: { type: Number, default: 48 },
  },

  data() {
    return {
      tab: 'library',
      mediaItems: [],
      selectedItems: [],
      activePreview: null,
      isLoading: false,
      isUploading: false,
      isDragging: false,
      uploadQueue: [],
      search: '',
      source: 'all',
      type: 'image',
      collection: '',
      currentPage: 1,
      pagination: {
        total: 0,
        per_page: 48,
        current_page: 1,
        from: 0,
        to: 0,
      },
      sourceOptions: [
        { value: 'all', text: 'All Sources' },
        { value: 'library', text: 'Library Uploads' },
        { value: 'product', text: 'Product Images' },
      ],
      typeOptions: [
        { value: '', text: 'All Types' },
        { value: 'image', text: 'Images' },
        { value: 'video', text: 'Videos' },
        { value: 'document', text: 'Documents' },
      ],
      collectionOptions: [
        { value: '', text: 'All Collections' },
        { value: 'library', text: 'Library' },
        { value: 'large_pictures', text: 'Product Images' },
        { value: 'slider_image', text: 'Slider Images' },
        { value: 'category_image', text: 'Category Images' },
        { value: 'sub_category_image', text: 'Sub Category Images' },
        { value: 'child_category_image', text: 'Child Category Images' },
      ],
    }
  },

  watch: {
    visible(value) {
      if (value) this.bootstrapModal()
    },
    preselected: {
      immediate: true,
      handler(value) {
        if (Array.isArray(value) && value.length) {
          this.selectedItems = [...value]
          this.activePreview = value[0]
        }
      },
    },
  },

  methods: {
    bootstrapModal() {
      this.tab = 'library'
      this.currentPage = 1
      this.uploadQueue = []
      this.isDragging = false

      if (Array.isArray(this.preselected) && this.preselected.length) {
        this.selectedItems = [...this.preselected]
        this.activePreview = this.preselected[0]
      } else {
        this.selectedItems = []
        this.activePreview = null
      }

      this.fetchLibrary()
    },

    async fetchLibrary() {
      this.isLoading = true
      try {
        const response = await fetchMedia({
          q: this.search || '',
          type: this.type || '',
          source: this.source || 'all',
          collection: this.collection || '',
          page: this.currentPage,
          show: this.perPage,
          sort: 'desc',
        })

        const payload = response?.data || {}
        const list = payload?.data || []
        const meta = payload?.meta?.pagination || payload?.meta || {}

        this.mediaItems = Array.isArray(list) ? list : []
        this.pagination = {
          total: Number(meta.total || this.mediaItems.length || 0),
          per_page: Number(meta.per_page || this.perPage),
          current_page: Number(meta.current_page || this.currentPage),
          from: Number(meta.from || 0),
          to: Number(meta.to || 0),
        }

        if (!this.activePreview && this.mediaItems.length) {
          this.activePreview = this.mediaItems[0]
        }
      } catch (error) {
        this.mediaItems = []
        this.pagination = {
          total: 0,
          per_page: this.perPage,
          current_page: 1,
          from: 0,
          to: 0,
        }
        this.notifyError(error, 'Could not load media library.')
      } finally {
        this.isLoading = false
      }
    },

    onSearchInput() {
      clearTimeout(searchDebounce)
      searchDebounce = setTimeout(() => {
        this.currentPage = 1
        this.fetchLibrary()
      }, 350)
    },

    onFilterChange() {
      this.currentPage = 1
      this.fetchLibrary()
    },

    onPageChange(page) {
      this.currentPage = page
      this.fetchLibrary()
    },

    handleMediaClick(item) {
      this.activePreview = item
      this.toggleSelection(item)
    },

    quickSelect(item) {
      this.activePreview = item
      if (!this.multiple) {
        this.selectedItems = [item]
        this.confirmSelection()
        return
      }
      this.toggleSelection(item)
    },

    toggleSelection(item) {
      if (!item || !item.id) return

      if (!this.multiple) {
        const alreadyOnlySelected = this.selectedItems.length === 1 && this.selectedItems[0].id === item.id
        this.selectedItems = alreadyOnlySelected ? [] : [item]
        return
      }

      const exists = this.selectedItems.some(selected => selected.id === item.id)
      if (exists) {
        this.selectedItems = this.selectedItems.filter(selected => selected.id !== item.id)
      } else {
        this.selectedItems = [...this.selectedItems, item]
      }
    },

    isSelected(item) {
      return this.selectedItems.some(selected => selected.id === item.id)
    },

    removeSelected(item) {
      this.selectedItems = this.selectedItems.filter(selected => selected.id !== item.id)
      if (this.activePreview && this.activePreview.id === item.id) {
        this.activePreview = this.selectedItems[0] || this.mediaItems[0] || null
      }
    },

    handleFileInput(event) {
      const files = Array.from(event.target.files || [])
      this.appendUploadQueue(files)
      event.target.value = null
    },

    handleDrop(event) {
      this.isDragging = false
      const files = Array.from(event.dataTransfer.files || [])
      this.appendUploadQueue(files)
    },

    appendUploadQueue(files) {
      if (!files.length) return
      this.uploadQueue = [...this.uploadQueue, ...files]
    },

    clearUploadQueue() {
      this.uploadQueue = []
    },

    async uploadSelectedFiles() {
      if (!this.uploadQueue.length) return

      const formData = new FormData()
      this.uploadQueue.forEach(file => {
        formData.append('files[]', file)
      })

      this.isUploading = true
      try {
        await uploadMedia(formData)
        this.uploadQueue = []
        this.tab = 'library'
        this.currentPage = 1
        this.source = 'all'
        this.collection = ''
        await this.fetchLibrary()
        this.notifySuccess('Media uploaded successfully.')
      } catch (error) {
        this.notifyError(error, 'Media upload failed.')
      } finally {
        this.isUploading = false
      }
    },

    confirmSelection() {
      this.$emit('select', [...this.selectedItems])
      this.$emit('close')
    },

    isImage(item) {
      return (item?.mime_type || '').startsWith('image/')
    },

    humanMime(mimeType) {
      if (!mimeType) return 'Unknown'
      if (mimeType.startsWith('image/')) return 'Image'
      if (mimeType.startsWith('video/')) return 'Video'
      return mimeType
    },

    handleImageError(event) {
      event.target.src = 'data:image/svg+xml;charset=UTF-8,%3csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3e%3crect width="100%25" height="100%25" fill="%23f3f2f7"/%3e%3ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%236e6b7b" font-family="Arial" font-size="18"%3ePreview unavailable%3c/text%3e%3c/svg%3e'
    },

    formatFileSize(size) {
      const bytes = Number(size || 0)
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
    },

    notifySuccess(message) {
      if (this.$bvToast) {
        this.$bvToast.toast(message, {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
      }
    },

    notifyError(error, fallbackMessage) {
      const message = error?.response?.data?.message || error?.message || fallbackMessage
      if (this.$bvToast) {
        this.$bvToast.toast(message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
}
</script>

<style scoped>
.media-picker-modal__body { padding: 0 !important; }
.media-picker { display: flex; min-height: 70vh; max-height: 75vh; }
.media-picker__main {
  flex: 1; min-width: 0; display: flex; flex-direction: column; border-right: 1px solid #ebe9f1;
}
.media-picker__sidebar { width: 320px; background: #fbfbfc; padding: 1rem; overflow-y: auto; }
.media-toolbar {
  padding: 1rem; border-bottom: 1px solid #ebe9f1; display: flex;
  align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap;
}
.media-toolbar__tabs,.media-toolbar__filters { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.media-search { min-width: 220px; }
.media-type-filter { min-width: 140px; }
.media-upload-tab,.media-library-tab { flex: 1; overflow-y: auto; padding: 1rem; }
.media-btn {
  min-height: 42px;
  border-radius: 14px;
  font-weight: 700;
  padding: 0.62rem 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer !important;
  transition: all 0.22s ease;
  border-width: 1px;
}
.media-btn:hover,
.media-btn:focus { transform: translateY(-1px); }
.media-btn--primary {
  background: linear-gradient(135deg, #7367f0, #8f80ff) !important;
  border-color: #7367f0 !important;
  color: #fff !important;
  box-shadow: 0 12px 26px rgba(115, 103, 240, 0.30);
}
.media-btn--primary:hover,
.media-btn--primary:focus {
  background: linear-gradient(135deg, #6558ea, #8274ff) !important;
  border-color: #6558ea !important;
  color: #fff !important;
  box-shadow: 0 16px 30px rgba(115, 103, 240, 0.36);
}
.media-btn--ghost,
.media-btn--tab.btn-outline-primary,
.media-btn--tab.btn-outline-secondary {
  background: linear-gradient(180deg, #ffffff, #f8f8fc) !important;
  border-color: #d9d4e8 !important;
  color: #5f5a6b !important;
  box-shadow: 0 8px 18px rgba(34, 41, 47, 0.06);
}
.media-btn--ghost:hover,
.media-btn--ghost:focus,
.media-btn--tab.btn-outline-primary:hover,
.media-btn--tab.btn-outline-primary:focus {
  background: #f5f4ff !important;
  border-color: #cfc9e4 !important;
  color: #4b465c !important;
  box-shadow: 0 12px 24px rgba(34, 41, 47, 0.08);
}
.media-btn--tab.btn-primary {
  background: linear-gradient(135deg, #7367f0, #8f80ff) !important;
  border-color: #7367f0 !important;
  color: #fff !important;
  box-shadow: 0 12px 26px rgba(115, 103, 240, 0.22);
}
.media-dropzone {
  min-height: 280px; border: 2px dashed #7367f0; border-radius: 0.9rem;
  background: rgba(115, 103, 240, 0.04); display: flex; align-items: center; justify-content: center; transition: 0.2s ease;
}
.media-dropzone.is-dragging { background: rgba(115, 103, 240, 0.08); border-color: #5e50ee; }
.media-dropzone__inner { text-align: center; padding: 2rem; max-width: 440px; }
.upload-queue { border: 1px solid #ebe9f1; border-radius: 0.75rem; overflow: hidden; }
.upload-queue__item {
  padding: 0.75rem 1rem; border-bottom: 1px solid #ebe9f1; display: flex; align-items: center; justify-content: space-between;
}
.upload-queue__item:last-child { border-bottom: none; }
.upload-queue__item-left { display: flex; align-items: center; min-width: 0; }
.media-library-meta { display: flex; align-items: center; justify-content: space-between; }
.media-grid-loading,.media-empty-state {
  min-height: 360px; display: flex; align-items: center; justify-content: center; flex-direction: column;
}
.media-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(155px, 1fr)); gap: 1rem; }
.media-grid__item {
  border: 1px solid #ebe9f1; border-radius: 1rem; overflow: hidden; cursor: pointer; background: #fff; transition: 0.2s ease;
  box-shadow: 0 8px 18px rgba(34, 41, 47, 0.03);
}
.media-grid__item:hover { transform: translateY(-2px); box-shadow: 0 12px 26px rgba(34, 41, 47, 0.08); }
.media-grid__item.is-selected { border-color: #7367f0; box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.15); }
.media-grid__item.is-active-preview { border-color: #5e50ee; }
.media-grid__thumb { position: relative; width: 100%; height: 140px; background: #f8f8f8; overflow: hidden; }
.media-grid__thumb img { width: 100%; height: 100%; object-fit: cover; }
.media-grid__file {
  width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: #f3f2f7; color: #6e6b7b;
}
.media-grid__check {
  position: absolute; right: 8px; top: 8px; width: 24px; height: 24px; background: #7367f0; color: #fff;
  border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(115, 103, 240, 0.35);
}
.media-grid__meta { padding: 0.75rem; }
.media-grid__name {
  font-size: 0.92rem; font-weight: 700; line-height: 1.25; word-break: break-word;
  display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden;
}
.media-grid__sub { font-size: 0.78rem; color: #6e6b7b; margin-top: 0.25rem; }
.media-sidebar-card {
  background: #fff; border: 1px solid #ebe9f1; border-radius: 1rem; padding: 1rem; box-shadow: 0 10px 22px rgba(34, 41, 47, 0.05);
}
.media-sidebar-preview {
  width: 100%; height: 180px; border-radius: 1rem; overflow: hidden; background: #f8f8f8; border: 1px solid #ebe9f1;
}
.media-sidebar-preview img { width: 100%; height: 100%; object-fit: contain; background:#fff; }
.media-sidebar-file-preview { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
.media-sidebar-info li { display: flex; flex-direction: column; margin-bottom: 0.75rem; word-break: break-word; }
.media-selected-item {
  display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 0.75rem 0; border-bottom: 1px solid #ebe9f1;
}
.media-selected-item:last-child { border-bottom: none; }
.media-selected-item__left { display: flex; align-items: center; min-width: 0; gap: 0.75rem; }
.media-selected-item__thumb {
  width: 42px; height: 42px; border-radius: 0.5rem; overflow: hidden; flex-shrink: 0; background: #f8f8f8; border: 1px solid #ebe9f1;
}
.media-selected-item__thumb img { width: 100%; height: 100%; object-fit: cover; }
.media-selected-item__thumb-fallback { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
.media-selected-item__meta { min-width: 0; }
.media-selected-item__name {
  font-size: 0.9rem; font-weight: 600; line-height: 1.25; word-break: break-word;
}
.media-picker-footer {
  border-top: 1px solid #ebe9f1; padding: 1rem 1.25rem; display: flex; align-items: center; justify-content: space-between;
  background: #fff;
}
@media (max-width: 991.98px) {
  .media-picker { flex-direction: column; max-height: unset; }
  .media-picker__main { border-right: none; border-bottom: 1px solid #ebe9f1; }
  .media-picker__sidebar { width: 100%; }
  .media-grid { grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); }
}
</style>
